import React, { useMemo } from 'react';
import { X, AlertTriangle, Clock, Car, Euro, Eye, Calendar, Shield } from 'lucide-react';
import { useFormat } from '../../../i18n/format.js';
import { useOrgCurrency } from '../../../hooks/useOrgCurrency.js';

function ExpiringContractsModal({ contracts, vehicleInsurances, intermediaries, onClose, onViewContract }) {
  const { t, fmt } = useFormat();
  const getIntermediary = (id) => intermediaries.find(i => i.id === id);
  // Primes de filiales différentes : conversion avant addition.
  const { sumScope } = useOrgCurrency();

  const { zoneRouge, zoneAmbre, totalPremiumAtRisk, totalVehiclesAtRisk } = useMemo(() => {
    const now = new Date();
    const rouge = [];
    const ambre = [];
    const atRisk = [];
    const vehicleIds = new Set();

    contracts.forEach(c => {
      if (!c.expiry_date) return;
      const daysLeft = Math.ceil((new Date(c.expiry_date) - now) / (1000 * 60 * 60 * 24));
      const vis = vehicleInsurances.filter(vi => vi.contract_id === c.id);
      const premium = sumScope(vis, vi => vi.premium_amount);
      vis.forEach(vi => { vehicleIds.add(vi.vehicle_id); atRisk.push(vi); });

      const enriched = { ...c, daysLeft, vehicleCount: vis.length, premium };
      if (daysLeft < 60) {
        rouge.push(enriched);
      } else {
        ambre.push(enriched);
      }
    });

    rouge.sort((a, b) => a.daysLeft - b.daysLeft);
    ambre.sort((a, b) => a.daysLeft - b.daysLeft);

    return {
      zoneRouge: rouge,
      zoneAmbre: ambre,
      totalPremiumAtRisk: sumScope(atRisk, vi => vi.premium_amount),
      totalVehiclesAtRisk: vehicleIds.size,
    };
  }, [contracts, vehicleInsurances, sumScope]);

  const ContractCard = ({ contract, zone }) => {
    const intermediary = getIntermediary(contract.intermediary_id);
    const isRouge = zone === 'rouge';
    const borderColor = isRouge ? 'border-red-300' : 'border-amber-300';
    const bgColor = isRouge ? 'bg-red-50' : 'bg-amber-50';

    return (
      <div className={`border-2 ${borderColor} ${bgColor} rounded-lg p-4 space-y-3`}>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-1">
              <span className="font-semibold text-gray-900">{contract.policy_number}</span>
              <span className={`px-2.5 py-1 text-xs font-bold rounded-full ${
                isRouge ? 'bg-red-500 text-white' : 'bg-amber-400 text-amber-900'
              }`}>
                {t('insurance.daysLeft', { n: contract.daysLeft })}
              </span>
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <span className="flex items-center gap-1">
                <Shield className="w-3.5 h-3.5" />
                {contract.insurer_name || '—'}
              </span>
              {intermediary && (
                <span className="text-gray-500">{intermediary.name}</span>
              )}
            </div>
          </div>
          <button
            onClick={() => onViewContract(contract)}
            className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
            title={t('insurance.viewContract')}
          >
            <Eye className="w-4 h-4" />
          </button>
        </div>

        <div className="flex items-center gap-6 text-sm">
          <span className="flex items-center gap-1.5 text-gray-700">
            <Car className="w-4 h-4 text-gray-400" />
            {t('insurance.expiringContractsModal.vehiclesConcerned', { count: contract.vehicleCount })}
          </span>
          <span className="flex items-center gap-1.5 text-gray-700">
            <Euro className="w-4 h-4 text-gray-400" />
            {contract.premium.label}
          </span>
          <span className="flex items-center gap-1.5 text-gray-700">
            <Calendar className="w-4 h-4 text-gray-400" />
            {fmt.date(contract.expiry_date)}
          </span>
        </div>

        <div className="flex items-center gap-2 pt-1">
          <span className="text-xs px-2 py-1 bg-white border border-gray-200 rounded text-gray-600">{t('insurance.expiringContractsModal.hintContactBroker')}</span>
          <span className="text-xs px-2 py-1 bg-white border border-gray-200 rounded text-gray-600">{t('insurance.expiringContractsModal.hintCheckRenewal')}</span>
          <span className="text-xs px-2 py-1 bg-white border border-gray-200 rounded text-gray-600">{t('insurance.expiringContractsModal.hintCompareOffers')}</span>
        </div>
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
    >
      <div className="bg-white rounded-xl shadow-2xl w-[850px] max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-red-500 px-6 py-4 rounded-t-xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-white" />
            <h2 className="text-lg font-semibold text-white">
              {t('insurance.expiringContractsModal.title', { count: contracts.length })}
            </h2>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Urgency Legend */}
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-2 text-sm">
              <span className="w-3 h-3 rounded-full bg-red-500"></span>
              <span className="font-medium text-red-700">{t('insurance.expiringContractsModal.legendCritical')}</span>
            </span>
            <span className="flex items-center gap-2 text-sm">
              <span className="w-3 h-3 rounded-full bg-amber-400"></span>
              <span className="font-medium text-amber-700">{t('insurance.expiringContractsModal.legendWarning')}</span>
            </span>
          </div>

          {/* Zone Rouge */}
          {zoneRouge.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-red-700 uppercase tracking-wide flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {t('insurance.expiringContractsModal.criticalZone', { count: zoneRouge.length })}
              </h3>
              {zoneRouge.map(c => (
                <ContractCard key={c.id} contract={c} zone="rouge" />
              ))}
            </div>
          )}

          {/* Zone Ambre */}
          {zoneAmbre.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-amber-700 uppercase tracking-wide flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                {t('insurance.expiringContractsModal.warningZone', { count: zoneAmbre.length })}
              </h3>
              {zoneAmbre.map(c => (
                <ContractCard key={c.id} contract={c} zone="ambre" />
              ))}
            </div>
          )}

          {contracts.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <AlertTriangle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p>{t('insurance.expiringContractsModal.empty')}</p>
            </div>
          )}

          {/* Bottom Summary */}
          {contracts.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-6">
                  <span className="text-gray-600">
                    {t('insurance.expiringContractsModal.premiumAtRisk')} <span className="font-semibold text-gray-900">{totalPremiumAtRisk.label}</span>
                  </span>
                  <span className="text-gray-600">
                    {t('insurance.expiringContractsModal.vehiclesAtRisk')} <span className="font-semibold text-gray-900">{totalVehiclesAtRisk}</span>
                  </span>
                </div>
                <span className="text-orange-600 font-medium text-xs">
                  {t('insurance.expiringContractsModal.actionRequired')}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ExpiringContractsModal;
